import React, { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { IconChoices } from '../../common/Icon'
import { MenuItem } from '../../common/MenuItem'
import { useModal } from '../../../hooks/useModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { UnableToLeaveSocietyModal } from '../LeaveSociety/UnableToLeaveSocietyModal'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { Spinner } from '../../common/Spinner'
import { ISocietiesModel } from '../../../interfaces/models/societies.interfaces'
import {
  canRemoveUserFromSociety,
  removeSelfFromSociety,
} from '../../../api/societies'
import { unitQueries } from '../../../api/units'
import { useAuth } from '../../../hooks/useAuth'
import { i18nSocietyType } from '../../../i18n'

interface LeaveSocietyMenuItemProps {
  society: ISocietiesModel
}

export const LeaveSocietyMenuItem = ({
  society,
}: LeaveSocietyMenuItemProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const {
    show: showLeaveConfirmationModal,
    open: openLeaveConfirmationModal,
    close: closeLeaveConfirmationModal,
  } = useModal()
  const {
    show: showUnableToLeaveModal,
    open: openUnableToLeaveModal,
    close: closeUnableToLeaveModal,
  } = useModal()
  const navigate = useNavigate()
  const { userId: authUserId } = useAuth()
  const userId = authUserId as string

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('leaveSociety.flashMessage.leaveSocietySuccess', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('leaveSociety.flashMessage.leaveSocietyFailure', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    )
  }

  const {
    mutate: mutateRemoveSelfFromSociety,
    isPending: isRemovingSelfFromSociety,
  } = useMutation({
    mutationFn: removeSelfFromSociety,
    onSuccess: () => {
      showSuccessMessage()
      navigate(reverseUrl('society'))
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const leaveSociety = async (): Promise<void> => {
    mutateRemoveSelfFromSociety({ societyId: society._id })
  }
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society._id)
  )

  const canLeaveSociety = canRemoveUserFromSociety(
    userId,
    society,
    societyUnits || []
  )

  const loading = isRemovingSelfFromSociety

  return (
    <>
      {!loading ? (
        <MenuItem
          title={translate('societySideBar.items.labels.leaveSociety', {
            context: i18nSocietyType(society.societyTypeDisplayName),
          })}
          icon={IconChoices.LEAVE_SOCIETY}
          destructive
          onClick={
            canLeaveSociety
              ? openLeaveConfirmationModal
              : openUnableToLeaveModal
          }
        />
      ) : (
        <Spinner />
      )}
      <ConfirmationModal
        title={translate('leaveSociety.confirmationModal.title', {
          context: i18nSocietyType(society.societyTypeDisplayName),
        })}
        description={translate('leaveSociety.confirmationModal.description', {
          context: i18nSocietyType(society.societyTypeDisplayName),
        })}
        show={showLeaveConfirmationModal}
        close={closeLeaveConfirmationModal}
        onConfirm={leaveSociety}
        deleteMode={false}
        confirmationButtonLabel={translate('common.actions.leave')}
      />
      <UnableToLeaveSocietyModal
        show={showUnableToLeaveModal}
        close={closeUnableToLeaveModal}
      />
    </>
  )
}
